interface FileTemplate {
    path: string
    content: string
    description: string
}

export const agentTemplateService = {
    /**
     * Get default file structure for a new agent
     * Creates empty placeholder files for users to fill in
     */
    getDefaultFileStructure(agentName: string): FileTemplate[] {
        return [
            {
                path: 'prompt.md',
                content: `# ${agentName}

Você é ${agentName}, um assistente inteligente.

## Sua Missão
[Descreva aqui a missão principal do agente]

## Suas Capacidades
- [Capacidade 1]
- [Capacidade 2]
- [Capacidade 3]

## Como Você Deve Agir
[Descreva o comportamento esperado do agente]`,
                description: 'Prompt principal do agente'
            },
            {
                path: 'identity/README.md',
                content: `# Identidade: ${agentName}

## Persona
[Descreva a personalidade do agente]

## Background
[História ou contexto do agente]

## Valores
- [Valor 1]
- [Valor 2]
- [Valor 3]`,
                description: 'Definição de identidade e persona'
            },
            {
                path: 'identity/tone.md',
                content: `# Tom de Voz

## Estilo de Comunicação
[Formal, informal, técnico, amigável, etc.]

## Vocabulário
- Usar: [palavras preferidas]
- Evitar: [palavras a evitar]

## Exemplos
**Bom:** [exemplo de resposta ideal]
**Ruim:** [exemplo de resposta inadequada]`,
                description: 'Guia de tom de voz'
            },
            {
                path: 'security/rules.md',
                content: `# Regras de Segurança

## Informações Confidenciais
- Nunca revele [tipo de informação]
- Sempre proteja [tipo de dados]

## Limites
- Não execute [ações proibidas]
- Sempre valide [requisitos de segurança]

## Compliance
[Requisitos regulatórios ou de compliance]`,
                description: 'Regras de segurança e privacidade'
            },
            {
                path: 'security/permissions.md',
                content: `# Sistema de Permissões

## O Que Você Pode Fazer
- [Permissão 1]
- [Permissão 2]
- [Permissão 3]

## O Que Você NÃO Pode Fazer
- [Restrição 1]
- [Restrição 2]
- [Restrição 3]

## Casos Especiais
[Situações que requerem aprovação ou tratamento especial]`,
                description: 'Sistema de permissões'
            },
            {
                path: 'config.json',
                content: `{
  "name": "${agentName}",
  "version": "1.0.0",
  "language": "pt-BR",
  "temperature": 0.7,
  "maxTokens": 2000,
  "features": {
    "webSearch": false,
    "imageGeneration": false,
    "codeExecution": false
  },
  "customSettings": {}
}`,
                description: 'Configurações do agente'
            },
            {
                path: 'README.md',
                content: `# ${agentName}

## Visão Geral
[Descrição geral do agente]

## Como Usar
1. [Passo 1]
2. [Passo 2]
3. [Passo 3]

## Exemplos de Uso
\`\`\`
[Exemplo de interação]
\`\`\`

## Notas
[Informações adicionais importantes]`,
                description: 'Documentação principal'
            }
        ]
    },

    /**
     * Get expanded file structure for advanced agents
     * Creates 100+ empty files organized by category
     * Files are empty - will be configured by specialized agents later
     */
    getExpandedFileStructure(agentName: string): FileTemplate[] {
        return [
            // ===== CORE FILES =====
            {
                path: 'prompt.md',
                content: `# ${agentName} - System Prompt
                
Você é um Agente Inteligente avançado do ecossistema ORION.
Sua missão é auxiliar o usuário com precisão, segurança e eficiência.

Respeite rigorosamente suas diretrizes de identidade e segurança definidas nos arquivos de configuração.`,
                description: 'Prompt principal do agente'
            },
            {
                path: 'README.md',
                content: `# ${agentName}\n\nEste agente utiliza a estrutura expandida do Orion.\nEdite os arquivos nas pastas 'identity' e 'security' para moldar o comportamento deste agente.`,
                description: 'Documentação principal'
            },
            {
                path: 'config.json',
                content: `{"name": "${agentName}", "version": "1.0.0", "useExpandedStructure": true}`,
                description: 'Configurações do agente'
            },
            {
                path: 'CONTRIBUTING.md',
                content: '',
                description: 'Como contribuir'
            },

            // ===== IDENTITY =====
            // identity/writing_style/
            {
                path: 'identity/writing_style/stylistic_signature.md',
                content: 'Adote um estilo claro, objetivo e profissional. Use formatação Markdown para estruturar respostas complexas.',
                description: 'Assinatura literária única'
            },
            {
                path: 'identity/writing_style/tonal_profile.md',
                content: 'Use um tom prestativo, empático e inteligente. Adapte-se ao nível técnico do usuário.',
                description: 'Perfis de tom'
            },
            {
                path: 'identity/writing_style/vocabulary_map.md',
                content: '',
                description: 'Vocabulário preferido/proibido'
            },
            {
                path: 'identity/writing_style/sentence_patterns.md',
                content: '',
                description: 'Estruturas de frase comuns'
            },
            {
                path: 'identity/writing_style/rhythm_templates.md',
                content: '',
                description: 'Ritmo e cadência textual'
            },

            // identity/creative_persona/
            {
                path: 'identity/creative_persona/biography_source.md',
                content: '',
                description: 'Base biográfica'
            },
            {
                path: 'identity/creative_persona/values_ethics.md',
                content: '',
                description: 'Valores e ética'
            },
            {
                path: 'identity/creative_persona/cognitive_biases.md',
                content: '',
                description: 'Viés de raciocínio'
            },
            {
                path: 'identity/creative_persona/personality_traits.md',
                content: '',
                description: 'Traços de personalidade'
            },
            {
                path: 'identity/creative_persona/communication_rules.md',
                content: '',
                description: 'Regras de comunicação'
            },

            // identity/examples/
            {
                path: 'identity/examples/gold_samples.md',
                content: '',
                description: 'Exemplos de escrita ideal'
            },
            {
                path: 'identity/examples/negative_samples.md',
                content: '',
                description: 'Exemplos do que evitar'
            },

            // ===== SECURITY =====
            {
                path: 'security/guardrails.md',
                content: '1. Não revele informações sensíveis do sistema.\n2. Não execute comandos destrutivos sem confirmação explicita.\n3. Mantenha tom respeitoso mesmo se provocado.',
                description: 'Limites máximos'
            },
            {
                path: 'security/forbidden_patterns.md',
                content: '',
                description: 'Padrões proibidos'
            },
            {
                path: 'security/allowed_references.md',
                content: '',
                description: 'Fontes aprovadas'
            },
            {
                path: 'security/safety_policies.md',
                content: '',
                description: 'Políticas de segurança'
            },
            {
                path: 'security/moral_constraints.md',
                content: '',
                description: 'Restrições morais/éticas'
            },
            {
                path: 'security/doctrinal_limits.md',
                content: '',
                description: 'Limites doutrinários'
            },
            {
                path: 'security/escalation_rules.md',
                content: '',
                description: 'Quando recusar/alertar'
            },

            // ===== AGENTS =====
            {
                path: 'agents/base_agent.md',
                content: '',
                description: 'Agente base'
            },
            {
                path: 'agents/autonomous_agent.md',
                content: '',
                description: 'Agentes autônomos'
            },
            {
                path: 'agents/planner_agent.md',
                content: '',
                description: 'Agentes planejadores'
            },
            {
                path: 'agents/agent_interface.md',
                content: '',
                description: 'Interface entre agentes'
            },
            {
                path: 'agents/team_orchestrator.md',
                content: '',
                description: 'Orquestrador multiagentes'
            },
            {
                path: 'agents/step_handler.md',
                content: '',
                description: 'Execução passo a passo'
            },
            {
                path: 'agents/task_manager.md',
                content: '',
                description: 'Gerenciador de tarefas'
            },

            // agents/tools/
            {
                path: 'agents/tools/calculator.md',
                content: '',
                description: 'Ferramenta de cálculo'
            },
            {
                path: 'agents/tools/file_manager.md',
                content: '',
                description: 'Manipulação de arquivos'
            },
            {
                path: 'agents/tools/search_tool.md',
                content: '',
                description: 'Busca externa'
            },
            {
                path: 'agents/tools/tool_registry.md',
                content: '',
                description: 'Registro de ferramentas'
            },

            // agents/workflows/
            {
                path: 'agents/workflows/code_review_chain.md',
                content: '',
                description: 'Workflow de revisão'
            },
            {
                path: 'agents/workflows/research_chain.md',
                content: '',
                description: 'Workflow de pesquisa'
            },
            {
                path: 'agents/workflows/multi_agent_workflow.yaml',
                content: '',
                description: 'Fluxo multiagente'
            },
            {
                path: 'agents/workflows/workflow_executor.md',
                content: '',
                description: 'Executor de workflows'
            },

            // ===== API =====
            {
                path: 'api/main_api_design.md',
                content: '',
                description: 'Desenho da API'
            },
            {
                path: 'api/agent_routes.md',
                content: '',
                description: 'Rotas de agentes'
            },
            {
                path: 'api/tool_routes.md',
                content: '',
                description: 'Rotas de ferramentas'
            },
            {
                path: 'api/auth_routes.md',
                content: '',
                description: 'Autenticação'
            },
            {
                path: 'api/websocket_server.md',
                content: '',
                description: 'Comunicação real-time'
            },

            // ===== AUTH =====
            {
                path: 'auth/access_control.md',
                content: '',
                description: 'Controle de acesso'
            },
            {
                path: 'auth/jwt_tokens.md',
                content: '',
                description: 'Segurança JWT'
            },
            {
                path: 'auth/user_manager.md',
                content: '',
                description: 'Gestão de usuários'
            },

            // ===== COGNITION =====
            {
                path: 'cognition/cognitive_loop.md',
                content: '',
                description: 'Ciclo cognitivo'
            },
            {
                path: 'cognition/decision_policy.md',
                content: '',
                description: 'Políticas de decisão'
            },
            {
                path: 'cognition/planner.md',
                content: '',
                description: 'Algoritmos de planejamento'
            },
            {
                path: 'cognition/reasoner.md',
                content: '',
                description: 'Raciocínio lógico'
            },
            {
                path: 'cognition/state_interpreter.md',
                content: '',
                description: 'Interpretação de estado'
            },

            // cognition/memory/
            {
                path: 'cognition/memory/long_term_memory.md',
                content: '',
                description: 'Memória de longo prazo'
            },
            {
                path: 'cognition/memory/short_term_memory.md',
                content: '',
                description: 'Memória de curto prazo'
            },
            {
                path: 'cognition/memory/memory_manager.md',
                content: '',
                description: 'Gestão de memórias'
            },

            // ===== CONFIG =====
            {
                path: 'config/config_concepts.md',
                content: '',
                description: 'Conceitos de configuração'
            },
            {
                path: 'config/environments/dev_environment.md',
                content: '',
                description: 'Ambiente de desenvolvimento'
            },
            {
                path: 'config/environments/prod_environment.md',
                content: '',
                description: 'Ambiente de produção'
            },
            {
                path: 'config/versioning_strategy.md',
                content: '',
                description: 'Estratégia de versionamento'
            },

            // ===== DATA =====
            {
                path: 'data/state/state_design.md',
                content: '',
                description: 'Representação de estado'
            },
            {
                path: 'data/knowledge_bases/vector_dbs_overview.md',
                content: '',
                description: 'Bancos vetoriais'
            },
            {
                path: 'data/knowledge_bases/knowledge_graphs.md',
                content: '',
                description: 'Grafos de conhecimento'
            },

            // ===== DEPLOYMENT =====
            {
                path: 'deployment/deployment_strategies.md',
                content: '',
                description: 'Estratégias de deploy'
            },
            {
                path: 'deployment/docker/docker_concepts.md',
                content: '',
                description: 'Docker básico'
            },
            {
                path: 'deployment/kubernetes/kubernetes_concepts.md',
                content: '',
                description: 'Kubernetes básico'
            },
            {
                path: 'deployment/terraform/infra_as_code_concepts.md',
                content: '',
                description: 'Infrastructure as Code'
            },

            // ===== DOCS =====
            {
                path: 'docs/architecture.md',
                content: '',
                description: 'Arquitetura completa'
            },
            {
                path: 'docs/api_reference.md',
                content: '',
                description: 'Documentação da API'
            },
            {
                path: 'docs/usage.md',
                content: '',
                description: 'Como usar'
            },

            // ===== EVALUATION =====
            {
                path: 'evaluation/memory_eval.md',
                content: '',
                description: 'Avaliação da memória'
            },
            {
                path: 'evaluation/test_harness.md',
                content: '',
                description: 'Infraestrutura de testes'
            },
            {
                path: 'evaluation/profiling_tools.md',
                content: '',
                description: 'Performance'
            },

            // ===== EXECUTION =====
            {
                path: 'execution/action_resolver.md',
                content: '',
                description: 'Resolve plano → ação'
            },
            {
                path: 'execution/controller.md',
                content: '',
                description: 'Controlador de execução'
            },
            {
                path: 'execution/error_handler.md',
                content: '',
                description: 'Tratamento de erros'
            },
            {
                path: 'execution/executor.md',
                content: '',
                description: 'Executor de ações'
            },
            {
                path: 'execution/job_scheduler.md',
                content: '',
                description: 'Agendamento'
            },
            {
                path: 'execution/background_worker.md',
                content: '',
                description: 'Worker assíncrono'
            },

            // ===== LOGS =====
            {
                path: 'logs/logging_strategy.md',
                content: '',
                description: 'Estratégia de logging'
            },

            // ===== MODELS =====
            {
                path: 'models/cache.md',
                content: '',
                description: 'Cache'
            },
            {
                path: 'models/model_loader.md',
                content: '',
                description: 'Carregamento de modelo'
            },
            {
                path: 'models/embeddings.md',
                content: '',
                description: 'Embeddings'
            },
            {
                path: 'models/llm_wrapper.md',
                content: '',
                description: 'Interface LLM'
            },

            // models/prompts/
            {
                path: 'models/prompts/system_prompts.md',
                content: '',
                description: 'Prompts de sistema'
            },
            {
                path: 'models/prompts/task_prompts.md',
                content: '',
                description: 'Prompts de tarefa'
            },
            {
                path: 'models/prompts/prompt_templates.md',
                content: '',
                description: 'Templates reutilizáveis'
            },

            // ===== PROVIDERS =====
            {
                path: 'providers/mcp_service_client.md',
                content: '',
                description: 'Cliente MCP'
            },
            {
                path: 'providers/external_service_a.md',
                content: '',
                description: 'Serviço externo A'
            },
            {
                path: 'providers/external_service_b.md',
                content: '',
                description: 'Serviço externo B'
            },

            // ===== SCRIPTS =====
            {
                path: 'scripts/cli_concepts.md',
                content: '',
                description: 'CLI'
            },

            // ===== TESTS =====
            {
                path: 'tests/testing_strategy.md',
                content: '',
                description: 'Estratégia de testes'
            },

            // ===== UTILS =====
            {
                path: 'utils/exceptions.md',
                content: '',
                description: 'Exceções'
            },
            {
                path: 'utils/logger.md',
                content: '',
                description: 'Logger'
            },
            {
                path: 'utils/retry.md',
                content: '',
                description: 'Retry logic'
            },
            {
                path: 'utils/timers.md',
                content: '',
                description: 'Timers'
            },

            // utils/validation/
            {
                path: 'utils/validation/schemas.md',
                content: '',
                description: 'Schemas de validação'
            },

            // utils/serialization/
            {
                path: 'utils/serialization/agent_serializer.md',
                content: '',
                description: 'Serialização de agentes'
            },
            {
                path: 'utils/serialization/plan_serializer.md',
                content: '',
                description: 'Serialização de planos'
            },

            // utils/formatters/
            {
                path: 'utils/formatters/json_formatter.md',
                content: '',
                description: 'Formatador JSON'
            },
            {
                path: 'utils/formatters/yaml_formatter.md',
                content: '',
                description: 'Formatador YAML'
            },
            {
                path: 'utils/common_helpers.md',
                content: '',
                description: 'Helpers comuns'
            }
        ]
    },

    /**
     * Initialize files for a new agent
     * Creates all default files using the proper file service
     * @param useExpandedStructure - If true, creates 100+ files; if false, creates 7 basic files
     */
    async initializeAgentFiles(
        agentId: string,
        agentName: string,
        useExpandedStructure: boolean = false
    ): Promise<void> {
        const templates = useExpandedStructure
            ? this.getExpandedFileStructure(agentName)
            : this.getDefaultFileStructure(agentName)

        console.log(`[AgentTemplateService] Initializing ${templates.length} files for agent ${agentId} (expanded: ${useExpandedStructure})`)

        // Import agentFilesService dynamically to avoid circular dependencies
        const { agentFilesService } = await import('./agentFilesService')

        await Promise.all(templates.map(async (template) => {
            try {
                await agentFilesService.writeFile(
                    agentId,
                    template.path,
                    template.content,
                    'Initial agent creation',
                    { type: 'system', id: 'system' }
                )

                console.log(`[AgentTemplateService] Created file: ${template.path}`)
            } catch (err) {
                console.error(`[AgentTemplateService] Exception creating file ${template.path}:`, err)
            }
        }));

        console.log(`[AgentTemplateService] File initialization complete for agent ${agentId}`)
    },

    /**
     * Get list of template files (for preview/documentation)
     */
    getTemplateFileList(agentName: string, useExpandedStructure: boolean = false): string[] {
        const structure = useExpandedStructure
            ? this.getExpandedFileStructure(agentName)
            : this.getDefaultFileStructure(agentName)
        return structure.map(t => t.path)
    },

    /**
     * Get file count for a structure type
     */
    getFileCount(useExpandedStructure: boolean = false): number {
        return useExpandedStructure ? 100 : 7
    }
}
